<!--
  ApiStoreSidebar — contextual sidebar contents for the "API" cauldron:
  the store first (public catalogue when signed out), then for signed-in users
  their API access, keys and usage, and the API documentation. These rows used
  to sit under Platform > Developers. API-only tenants never see this: they get
  `features/api-store/components/ApiSidebar.vue` instead of the rail.
-->
<template>
  <div class="flex flex-col h-full">
    <nav class="flex-1 overflow-y-auto px-3 py-3" :aria-label="t('cauldron.api.label')">
      <div v-if="!collapsed" class="sidebar-section-label">
        {{ t('cauldron.api.label') }}
      </div>
      <sidebar-menu-item
        v-if="isApiDocsCatalogEnabled"
        :to="catalogRoute"
        :label="isAuthenticated ? t('api_store.nav.store') : t('sidebar.developers.catalog')"
        :icon="isAuthenticated ? mdiStorefrontOutline : mdiCodeBraces"
        :collapsed="collapsed"
        active-match="prefix"
        @click="emit('navigate')"
      />
      <template v-if="isAuthenticated">
        <sidebar-menu-item
          v-if="showApiAccess"
          to="/api/access"
          :label="t('api_store.nav.my_api_access')"
          :icon="mdiDatabaseCheckOutline"
          :collapsed="collapsed"
          active-match="prefix"
          @click="emit('navigate')"
        />
        <sidebar-menu-item
          to="/account/api-keys"
          :label="t('api_keys.menu_label')"
          :icon="mdiKeyOutline"
          :collapsed="collapsed"
          active-match="prefix"
          @click="emit('navigate')"
        />
        <sidebar-menu-item
          v-if="isApiDocsCatalogEnabled"
          to="/api/usage"
          :label="t('api_store.nav.api_usage')"
          :icon="mdiChartBar"
          :collapsed="collapsed"
          active-match="prefix"
          @click="emit('navigate')"
        />
      </template>
      <sidebar-menu-item
        :to="documentationRoute"
        :label="t('sidebar.developers.documentation')"
        :icon="mdiBookOpenOutline"
        :collapsed="collapsed"
        active-match="includes"
        match-key="/documentation"
        @click="emit('navigate')"
      />
    </nav>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import SidebarMenuItem from '@/components/navigation/SidebarMenuItem.vue';
  import { useFeatureFlags } from '@/composables/useFeatureFlags';
  import {
    mdiBookOpenOutline,
    mdiChartBar,
    mdiCodeBraces,
    mdiDatabaseCheckOutline,
    mdiKeyOutline,
    mdiStorefrontOutline,
  } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { hasDatasetApiGrant } from '@/features/api-store/domain/datasetAccess';
  import { useAuthStore } from '@/stores/useAuthStore';
  import { useEntitlementsStore } from '@/stores/useEntitlementsStore';

  defineProps({
    collapsed: { type: Boolean, default: false },
  });
  const emit = defineEmits(['navigate']);

  const { t } = useI18n();
  const authStore = useAuthStore();
  const { isApiDocsCatalogEnabled } = useFeatureFlags();
  const entitlementsStore = useEntitlementsStore();

  const isAuthenticated = computed(() => authStore.isAuthenticated);

  // "My API access" outlives the store flag: a user granted API access to a
  // dataset (`dataset.<id>.api`) must find it while the store is hidden.
  const showApiAccess = computed(
    () => isApiDocsCatalogEnabled.value || hasDatasetApiGrant(entitlementsStore.allowed),
  );

  // The API-docs site has both a public and an authenticated landing.
  const catalogRoute = computed(() => (isAuthenticated.value ? '/api/store' : '/api-docs/catalog'));
  const documentationRoute = computed(() =>
    isAuthenticated.value ? '/api-docs-auth/documentation' : '/api-docs/documentation',
  );
</script>
